import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme as T } from '../../theme';
import { useAuth } from '../../context/AuthContext';
import { useDesktopTopBarProps } from '../../context/DesktopLayoutContext';
import UserProfileDropdown, { getInitials } from './UserProfileDropdown';
import OutletDropdown from '../OutletDropdown';
import DateRangeControl from './DateRangeControl';
import { QuickSelectOption } from './DateRangePopover';
import { reportsPaths } from '../reports/paths';
import KpiTooltip from '../KpiTooltip';

const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];

/**
 * Quick-select sets (GLOBAL-DP-1). These are the SAME arrays the report screens already
 * pass as `dateFilters.options`, so retargeting the vocabulary here retargets every
 * report at once — no screen-by-screen edit needed.
 *
 * The stored tokens are unchanged ('7D', 'This Month', …); only the rendered label moves
 * to the spec's wording, which keeps the ~200 existing `activeDate === '7D'` branches
 * across the report screens working untouched.
 *
 * "Custom" is gone as an option: an arbitrary range is now reached by picking days in the
 * calendar, which every report offers.
 *
 * THE TWO SETS DIFFER BY EXACTLY ONE TOKEN — `Today` (`DPFIX-08`). That is the whole
 * contract, and it is deliberate: a reader who opens Snapshot and then Product must be
 * able to see at a glance that the second screen offers one fewer option rather than a
 * different vocabulary. Adding a token to one set without adding it to the other
 * reintroduces the "every report has its own random list" defect.
 */
export const DATE_PRESETS_DESKTOP = ['Today', 'Yesterday', '7D', '30D', 'This Month', 'This Year'];
export const DATE_PRESETS_MOBILE = DATE_PRESETS_DESKTOP;

/**
 * Reports that cannot serve the live day (Product, Events — `excludeToday`)
 * get the same set with `Today` DROPPED. `Yesterday` is not a substitute bolted on for
 * those screens; it is a first-class member of both sets, so the single-day shape (and
 * with it the week-over-week comparison) is reachable everywhere.
 */
export const DATE_PRESETS_DESKTOP_NO_TODAY = DATE_PRESETS_DESKTOP.filter((t) => t !== 'Today');
export const DATE_PRESETS_MOBILE_NO_TODAY = DATE_PRESETS_DESKTOP_NO_TODAY;

/**
 * Customers used to carry a THIRD set that dropped `This Year` on top of `Today`
 * (`DPFIX-11`), because `useCustomerInsights.getCustomerDateRange` had no `thisyear`
 * branch: the token fell through to that function's `{ today, today }` default, so the
 * control displayed a whole year while the screen fetched a single day. That branch now
 * exists and delegates to the shared `getThisYearRange`, so Customers is back on the
 * `NO_TODAY` set above and the vocabulary is TWO sets again, differing by exactly `Today`.
 * Customers has since moved to the FULL set (CUST-10.1 lists Today), so it no longer
 * passes `excludeToday` at all.
 *
 * If a report ever needs a narrower set again: derive it from the set above (never restate
 * it as a literal), and remember that removing a pill is only HALF the job. The shared
 * cross-report filter retains any resolvable token regardless of a screen's `options`
 * (GLOBAL-DP-5), so an unfetchable token must ALSO go in `useSharedReportDateFilter`'s
 * `excludeTokens`. Customers carried `Last 3 Months` / `Last 6 Months` / `Last Year` there
 * until its KPI row moved onto `resolveTokenRange`, which serves them.
 */

/** EOD Reconciliation keeps its extended long-range set (GLOBAL-DP-6). */
export const DATE_PRESETS_EOD = ['Yesterday', 'This Month', 'Last Month', 'Last 3 Months', 'Last 6 Months', 'This Year', 'Last Year'];

/** Token → the label shown on its quick-select button. */
const QUICK_SELECT_LABELS: Record<string, string> = {
  'Today': 'Today',
  'Yesterday': 'Yesterday',
  '7D': 'Last 7 days',
  '30D': 'Last 30 days',
  'This Month': 'This month',
  'This Year': 'This year',
  'Last Month': 'Last month',
  'Last 3 Months': 'Last 3 months',
  'Last 6 Months': 'Last 6 months',
  'Last Year': 'Last year',
  // Guest Reviews only (`#UD 12`). 'All' names no range — see `isUnboundedToken` in
  // utils/reportPeriod.ts; it is a label entry here so the pill RENDERS, which is the
  // half `toQuickSelects` was silently dropping.
  '90D': 'Last 90 days',
  'All': 'All',
};

/**
 * Map a page's preset token list to the quick-select options rendered in the overlay.
 *
 * The `.filter()` DROPS ANY TOKEN WITH NO LABEL ENTRY, SILENTLY. A screen whose set
 * contains a token this map does not know renders a SHORTER row with no warning — the
 * page looks like it was given the default set rather than like it was misconfigured.
 * That is the mechanism behind the open Guest Reviews defect (`UD 12`, on the rep-stg
 * pair): `GBPReviewsPage` passes `All / Today / 7D / 30D / 90D`, and because `All` and
 * `90D` are absent from `QUICK_SELECT_LABELS` — and unresolvable in
 * `resolveTokenRange` — only three pills survive.
 *
 * So: adding a preset is always a THREE-site change — the set, `QUICK_SELECT_LABELS`,
 * and `resolveTokenRange` in `utils/reportPeriod.ts`. Two out of three fails quietly.
 */
const toQuickSelects = (tokens: string[] | undefined, fallback: string[]): QuickSelectOption[] =>
  (tokens?.length ? tokens : fallback)
    .filter((t) => QUICK_SELECT_LABELS[t])
    .map((t) => ({ token: t, label: QUICK_SELECT_LABELS[t] }));

/**
 * Centralized TopBar: shell + built-in date filters. Same component for desktop and mobile.
 * Desktop: content from props (passed by DesktopLayout from context).
 * Mobile: content from context; back and profile built internally; location selector (OutletDropdown) in third row.
 *
 * @param {Object} props
 * @param {'desktop'|'mobile'} [props.variant] - Layout variant
 * @param {React.ReactNode} [props.leftAddon] - e.g. back button (desktop only)
 * @param {string} [props.breadcrumbs] - e.g. "Dashboard" or "Reports › Menu" (desktop only)
 * @param {string} [props.title] - current page title (desktop only; mobile uses context)
 * @param {string} [props.subtitle] - optional subtitle (desktop only)
 * @param {React.ReactNode} [props.rightAddon] - (desktop only; mobile uses internal profile)
 * @param {Object} [props.dateFilters] - unified date control (desktop from props; mobile from context)
 *   @param {string} props.dateFilters.value - stored filter token ('Today' | '7D' | 'custom:from:to' | ...)
 *   @param {(token: string) => void} props.dateFilters.onChange
 *   @param {string[]} [props.dateFilters.options] - this page's quick-select token set; defaults to DATE_PRESETS_DESKTOP (or the NO_TODAY set when excludeToday)
 *   @param {boolean} [props.dateFilters.allowFutureDates] - when true, the calendar allows future days (Reservations)
 *   @param {boolean} [props.dateFilters.excludeToday] - when true, the calendar caps at the last finalised day (today is excluded)
 *   @param {'shape'|'preceding'} [props.dateFilters.comparisonBasis] - which window the report's deltas compare against, so the
 *     comparison line names it (DPFIX-13). Default 'shape'; no report passes 'preceding' today (Customers did until QA #10).
 * @param {Object} [props.sourceFilters] - { options: string[], value: string, onChange: (v) => void }
 * @param {() => void} [props.onExport] - show Export button if provided
 * @param {React.ReactNode} [props.extraContent] - optional node between date/source filters and Export
 * @param {React.ReactNode} [props.bottomSection] - optional full-width row below main bar (desktop/mobile)
 * @param {string} [props.titleInfo] - optional page-level note; renders a KpiTooltip info icon
 *   beside the title on desktop and mobile. Mobile reads it from context like the title does.
 */
export default function TopBar({
  variant = 'desktop',
  leftAddon,
  breadcrumbs,
  title,
  titleInfo,
  subtitle,
  rightAddon,
  dateFilters,
  sourceFilters,
  extraContent,
  onExport,
  bottomSection,
}: any) {
  const isMobile = variant === 'mobile';
  const topBarPropsFromContext = useDesktopTopBarProps();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, logout } = useAuth();

  const dateFiltersSource = isMobile ? topBarPropsFromContext.dateFilters : dateFilters;
  const allowFutureDates = dateFiltersSource?.allowFutureDates || false;
  const excludeToday = dateFiltersSource?.excludeToday || false;
  const comparisonBasis = dateFiltersSource?.comparisonBasis ?? 'shape';
  const quickSelects = toQuickSelects(
    dateFiltersSource?.options,
    excludeToday ? DATE_PRESETS_DESKTOP_NO_TODAY : DATE_PRESETS_DESKTOP
  );

  const [showBottomMenu, setShowBottomMenu] = useState(false);

  if (isMobile) {
    const mobileTitle = topBarPropsFromContext.title ?? title ?? 'Reports';
    const mobileTitleInfo = topBarPropsFromContext.titleInfo ?? titleInfo;
    const mobileDateFilters = topBarPropsFromContext.dateFilters ?? dateFilters;
    const onBack = topBarPropsFromContext.onBack;
    const initials = getInitials(auth?.fullName ?? auth?.userId ?? auth?.userName ?? 'U');
    const displayName = auth?.fullName ?? auth?.userId ?? auth?.userName ?? 'User';
    const userRole = auth?.role ?? auth?.designation ?? 'Restaurant Manager';

    const isSnapshotPage = location.pathname.startsWith('/reports/snapshot');
    const hasCustomBack = typeof onBack === 'function';
    const showBack = hasCustomBack || (!isSnapshotPage && topBarPropsFromContext.showBack !== false);
    const backButton = (
      <button
        type="button"
        onClick={() => (typeof onBack === 'function' ? onBack() : navigate(-1))}
        aria-label="Back"
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: T.surface,
          border: `1px solid ${T.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t3 }}>chevron_left</span>
      </button>
    );

    return (
      <div className="mobile-topbar" style={{ background: T.surface, borderBottom: `1px solid ${T.div}`, position: 'sticky', top: 0, zIndex: 100 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px 8px',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {showBack && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>{backButton}</div>}
            <div style={{ fontSize: 22, fontWeight: 700, color: T.t1, display: 'flex', alignItems: 'center', gap: 6 }}>
              {mobileTitle}
              {mobileTitleInfo && <KpiTooltip text={mobileTitleInfo} isMobile placement="right" />}
            </div>
          </div>
          {/* Circular Profile Trigger */}
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => setShowBottomMenu(!showBottomMenu)}
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                background: T.hover,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: T.t2,
                cursor: 'pointer',
                border: showBottomMenu ? `1.5px solid ${T.accent}` : `1px solid ${T.divider}`,
              }}
            >
              {initials}
            </div>

            {/* Profile dropdown — appears below the avatar */}
            {showBottomMenu && (
              <>
                <div
                  onClick={() => setShowBottomMenu(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 36,
                    right: 0,
                    width: 190,
                    background: T.surface,
                    borderRadius: 12,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                    border: `1px solid ${T.divider}`,
                    zIndex: 1000,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ padding: '12px 14px 4px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{displayName}</div>
                    <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{userRole}</div>
                  </div>
                  {/* Integrations (incl. Google Business Profile) are super-only and
                      surfaced under Settings — only on the Guest Reviews page. */}
                  {location.pathname === reportsPaths.reviews && SUPER_ROLES.includes(auth?.role ?? '') && (
                    <button
                      onClick={() => { setShowBottomMenu(false); navigate('/integrations'); }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: 'transparent',
                        border: 'none',
                        borderTop: `1px solid ${T.divider}`,
                        cursor: 'pointer',
                        color: T.t1,
                        fontSize: 12,
                        fontWeight: 600,
                        marginTop: 8,
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: 16 }}>settings</span>
                      Settings
                    </button>
                  )}
                  <button
                    onClick={() => { logout(); navigate('/login', { replace: true }); }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'transparent',
                      border: 'none',
                      borderTop: `1px solid ${T.divider}`,
                      cursor: 'pointer',
                      color: T.red,
                      fontSize: 12,
                      fontWeight: 600,
                      marginTop: 8,
                    }}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: 16 }}>logout</span>
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {mobileDateFilters && (
          <div style={{ padding: '0 16px 8px' }}>
            <DateRangeControl
              variant="mobile"
              value={mobileDateFilters.value}
              onChange={mobileDateFilters.onChange}
              quickSelects={quickSelects}
              excludeToday={excludeToday}
              allowFutureDates={allowFutureDates}
              comparisonBasis={comparisonBasis}
            />
          </div>
        )}

        {topBarPropsFromContext.sourceFilters && topBarPropsFromContext.sourceFilters.options?.length > 0 && (
          <div style={{ padding: '0 16px 8px' }}>
            <div style={{ display: 'flex', background: T.hover, borderRadius: 7, padding: 2 }}>
              {topBarPropsFromContext.sourceFilters.options.map((opt) => {
                const active = topBarPropsFromContext.sourceFilters.value === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => topBarPropsFromContext.sourceFilters.onChange(opt)}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: 'none',
                      background: active ? T.surface : 'transparent',
                      color: active ? T.accent : T.t2,
                      boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                      fontFamily: T.f,
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!topBarPropsFromContext.hideOutlet && (
          <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <OutletDropdown />
            </div>
            {topBarPropsFromContext.outletRightAddon}
          </div>
        )}

        {bottomSection && (
          <div style={{ padding: '0 16px 8px' }}>{bottomSection}</div>
        )}
      </div>
    );
  }

  /* Desktop */
  return (
    <div
      style={{
        height: 56,
        flexShrink: 0,
        background: T.surface,
        borderBottom: `1px solid ${T.div}`,
        boxShadow: T.tbSh,
        position: 'relative',
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        gap: 16,
        fontFamily: T.f,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 'auto' }}>
        {leftAddon}
        {breadcrumbs != null && breadcrumbs !== '' && (() => {
          const parts = breadcrumbs.split('›').map(s => s.trim());
          return (
            <>
              {parts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span style={{ fontSize: 12, color: T.t3 }}>›</span>}
                  <span style={{ fontSize: 12, color: i === parts.length - 1 && parts.length > 1 ? T.accent : T.t3 }}>{part}</span>
                </React.Fragment>
              ))}
              <span style={{ fontSize: 12, color: T.t3 }}>›</span>
            </>
          );
        })()}
        <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>{title ?? (breadcrumbs === 'Loyalty' ? 'Loyalty' : 'Dashboard')}</span>
        {titleInfo && <KpiTooltip text={titleInfo} placement="right" />}
      </div>

      {dateFilters && (
        <DateRangeControl
          variant="desktop"
          value={dateFilters.value}
          onChange={dateFilters.onChange}
          quickSelects={quickSelects}
          excludeToday={excludeToday}
          allowFutureDates={allowFutureDates}
          comparisonBasis={comparisonBasis}
        />
      )}

      {sourceFilters && (
        <div style={{ display: 'flex', background: T.hover, borderRadius: 6, padding: 2 }}>
          {sourceFilters.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => sourceFilters.onChange(opt)}
              style={{
                padding: '4px 12px',
                borderRadius: 5,
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 500,
                border: 'none',
                background: sourceFilters.value === opt ? T.surface : 'transparent',
                color: sourceFilters.value === opt ? T.accent : T.t2,
                boxShadow: sourceFilters.value === opt ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                fontFamily: T.f,
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {extraContent}

      {/* Export button — hidden for now
      {onExport && (
        <button
          type="button"
          onClick={onExport}
          style={{
            padding: '6px 14px',
            borderRadius: 8,
            cursor: 'pointer',
            border: `1px solid ${T.border}`,
            fontSize: 11.5,
            color: T.t2,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: T.surface,
            fontFamily: T.f,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path
              d="M6 2v6M3 5l3 3 3-3M2 10h8"
              fill="none"
              stroke={T.t3}
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Export
        </button>
      )}
      */}
    </div>
  );
}
